import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../utils/api';
import { getMessages, saveMessage, saveConversation, getInvitedPerson, recordInvitedPerson } from '../utils/db';
import { setInviteCookie, hasInviteCookie, getInviteCookieValue } from '../utils/cookies';
import { getDeviceId } from '../utils/deviceId';
import { useNotification } from '../contexts/NotificationContext';
import useNotificationPermission from '../hooks/useNotificationPermission';
import NotificationPromptModal from '../components/NotificationPromptModal';
import { registerFcmToken } from '../utils/fcm';

const serverUrl = process.env.REACT_APP_SOCKET_URL || `https://${window.location.hostname || '192.168.0.102'}:8084`;
const socket = io(serverUrl, {
  autoConnect: false,
});

function InvitePage() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('loading'); // loading, name-input, chat
  const [name, setName] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [error, setError] = useState('');
  const [online, setOnline] = useState(false);
  const messagesEndRef = React.useRef(null);

  // 👇 Notification hooks — must be called before any hook that depends on them
  const { addNotification } = useNotification();

  // When notification permission is granted, register the FCM token with the
  // backend mapped to the device ID so the backend can send push notifications
  // to this device even when the user is not authenticated (invited guest).
  const handleNotificationGranted = useCallback(async () => {
    try {
      const deviceId = getDeviceId();
      await registerFcmToken(deviceId);
      console.log('📱 Invite guest FCM registered with device ID:', deviceId);
    } catch (err) {
      console.warn('📱 Invite guest FCM registration failed:', err.message);
      addNotification(
        '🔔 Push notifications could not be set up. You may miss messages when the app is in the background.',
        'warning',
        8000
      );
    }
  }, [addNotification]);

  const {
    showPrompt,
    isPrompting,
    dismissPrompt,
    requestPermission,
    phase,
    showModal,
  } = useNotificationPermission({
    autoRequest: true,
    onGranted: handleNotificationGranted,
  });

  // Validate invite on mount via backend API
  useEffect(() => {
    const validateInvite = async () => {
      try {
        // Check if this device has already joined this invite (IndexedDB + cookie)
        const existingRecord = await getInvitedPerson(inviteCode);
        const hasCookie = hasInviteCookie(inviteCode);

        if (existingRecord || hasCookie) {
          // This device already joined this invite — rejoin the existing conversation
          const record = existingRecord || {
            participantName: 'a participant',
            conversationId: hasCookie ? getInviteCookieValue(inviteCode) : null,
            participantId: null,
            hostName: 'the host',
          };

          setConversationId(record.conversationId);
          // If we have a stored participantId, use it; otherwise generate a temp one for socket registration
          setParticipantId(record.participantId || `guest_rejoin_${Date.now()}`);
          setInviteData({ created_by_name: record.hostName });

          // Load existing messages
          if (record.conversationId) {
            const storedMessages = await getMessages(record.conversationId);
            setMessages(storedMessages);
          }

          setStep('chat');
          return;
        }

        const data = await api.getInvite(inviteCode);
        if (!data) {
          setError('Invalid invite link. This invite does not exist.');
          setStep('error');
          return;
        }
        if (!data.active) {
          setError('This invite has expired or been deactivated.');
          setStep('error');
          return;
        }
        setInviteData(data);
        setStep('name-input');
      } catch (err) {
        setError(err.message || 'Failed to validate invite. Please try again.');
        setStep('error');
      }
    };
    validateInvite();
  }, [inviteCode]);

  // Connect socket and join room
  useEffect(() => {
    if (step !== 'chat' || !conversationId) return;

    socket.connect();

    // Log all incoming socket events for debugging
    socket.onAny((event, ...args) => {
      console.log(`[Socket Event] ${event}`, args.length > 0 ? args[0] : '');
    });

    socket.on('connect', () => {
      console.log('Connected to socket server');
      // Register as an anonymous (guest) user with explicit kind
      socket.emit('register_user', {
        user_id: participantId,
        kind: 'anonymous',
      });
      // Join the invite room
      socket.emit('join_room', conversationId);
    });

    socket.on('room_joined', (data) => {
      console.log('Joined room:', data);
      setOnline(true);
    });

    socket.on('user_online', () => {
      setOnline(true);
    });

    socket.on('new_message', (data) => {
      if (data.user_id !== participantId) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
          text: data.message,
          sender: 'them',
          timestamp: data.timestamp || Date.now(),
        }]);
      }
    });

    // Handle pending messages (rehydrated from Firestore when host comes online)
    socket.on('pending_message', (data) => {
      const msgText = data.text || '';
      const msgSender = data.sender || 'them';
      if (msgSender !== 'me') {
        setMessages(prev => [...prev, {
          id: data.id || Date.now().toString(36),
          text: msgText,
          sender: 'them',
          timestamp: data.created_at || Date.now(),
        }]);
      }
    });

    return () => {
      socket.off('connect');
      socket.off('room_joined');
      socket.off('user_online');
      socket.off('new_message');
      socket.off('pending_message');
      socket.disconnect();
    };
  }, [step, conversationId, participantId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleJoin = async () => {
    if (!name.trim()) return;

    try {
      // Get the consistent device-level guest ID for this device
      const deviceId = getDeviceId();

      // Join via backend API (which persists to Firestore)
      // Pass the device ID as participant_id so the same device
      // gets a consistent guest ID across sessions.
      const result = await api.joinInvite(inviteCode, name.trim(), deviceId);
      setConversationId(result.conversation_id);
      setParticipantId(result.participant_id);

      // Record this invite in IndexedDB so we can rejoin later
      await recordInvitedPerson({
        inviteCode,
        conversationId: result.conversation_id,
        participantName: name.trim(),
        participantId: result.participant_id,
        hostName: inviteData?.created_by_name || 'Unknown',
      });

      // Set a cookie as a secondary identifier for this device
      setInviteCookie(inviteCode, result.conversation_id);

      // Save the conversation locally so it appears in the guest's chat history
      // Use the guest's own name as the userId so the guest dashboard can find it
      await saveConversation({
        id: result.conversation_id,
        name: inviteData?.created_by_name || 'Unknown',
        userId: result.participant_id,
        guestName: name.trim(),
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(inviteData?.created_by_name || 'Unknown')}&background=1e293b&color=fff`,
        lastMessage: '',
        updatedAt: Date.now(),
        inviteCode,
        isInvite: true,
      });

      // Load any existing messages from IndexedDB
      const storedMessages = await getMessages(result.conversation_id);
      setMessages(storedMessages);

      setStep('chat');
    } catch (err) {
      setError(err.message || 'Failed to join chat');
      setStep('error');
    }
  };

  const handleSend = async () => {
    if (!inputText.trim() || !socket || !socket.connected) return;

    const text = inputText;
    setInputText('');

    // Save locally
    const savedMsg = await saveMessage({
      conversationId,
      text,
      sender: 'me',
    });
    setMessages(prev => [...prev, savedMsg]);

    // Send via socket
    socket.emit('chat_message', {
      user_id: participantId,
      message: text,
      timestamp: Date.now(),
      conversation_id: conversationId,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  // Shared notification modal and bell — rendered across all steps
  const notificationUI = (
    <>
      <NotificationPromptModal
        show={showPrompt}
        isPrompting={isPrompting}
        onEnable={requestPermission}
        onDismiss={dismissPrompt}
        isDenied={phase === 'denied'}
      />
      {phase !== 'granted' && phase !== 'unsupported' && (
        <button
          onClick={showModal}
          style={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 9999,
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: 'none',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 28px rgba(99, 102, 241, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.4)';
          }}
          aria-label="Enable notifications"
          title={phase === 'denied' ? 'Notifications are blocked — click to learn more' : 'Enable notifications'}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </button>
      )}
    </>
  );

  // Loading state
  if (step === 'loading') {
    return (
      <>
        {notificationUI}
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.loadingSpinner} />
            <p style={styles.loadingText}>Validating invite link...</p>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (step === 'error') {
    const alreadyJoined = error.includes('already joined');
    return (
      <>
        {notificationUI}
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.errorIcon}>{alreadyJoined ? '🔁' : '⚠️'}</div>
            <h2 style={styles.errorTitle}>{alreadyJoined ? 'Already Joined' : 'Invite Error'}</h2>
            <p style={styles.errorText}>{error}</p>
            <button style={styles.button} onClick={() => navigate(alreadyJoined ? '/guest' : '/')}>
              {alreadyJoined ? 'Go to My Chats' : 'Go Home'}
            </button>
          </div>
        </div>
      </>
    );
  }

  // Name input state
  if (step === 'name-input') {
    return (
      <>
        {notificationUI}
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.inviteIcon}>💬</div>
            <h2 style={styles.title}>You're invited!</h2>
            <p style={styles.subtitle}>
              <strong>{inviteData?.created_by_name}</strong> wants to chat with you.
            </p>
            <p style={styles.label}>Enter your name to join:</p>
            <input
              style={styles.input}
              type="text"
              placeholder="Your name"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleJoin()}
              autoFocus
            />
            <button
              style={styles.button}
              onClick={handleJoin}
              disabled={!name.trim()}
            >
              Join Chat
            </button>
          </div>
        </div>
      </>
    );
  }

  // Chat state
  return (
    <>
      {notificationUI}
      <div style={styles.chatContainer}>
      <div style={styles.chatHeader}>
        <button style={styles.backBtn} onClick={() => navigate('/guest')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div style={styles.headerInfo}>
          <h3 style={styles.headerName}>Chat with {inviteData?.created_by_name}</h3>
          <span style={{ ...styles.statusDot, background: online ? '#4CAF50' : '#666' }} />
          <span style={styles.statusText}>{online ? 'Online' : 'Connecting...'}</span>
        </div>
      </div>

      <div style={styles.messagesArea}>
        {messages.length === 0 ? (
          <div style={styles.emptyState}>
            No messages yet. Say hello!
          </div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              style={{
                ...styles.messageBubble,
                alignSelf: msg.sender === 'me' ? 'flex-end' : 'flex-start',
                background: msg.sender === 'me'
                  ? 'linear-gradient(135deg, #ff8a00, #e52e71)'
                  : '#2a2d3e',
                borderBottomRightRadius: msg.sender === 'me' ? 4 : 20,
                borderBottomLeftRadius: msg.sender === 'me' ? 20 : 4,
              }}
            >
              {msg.text}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputArea}>
        <input
          style={styles.messageInput}
          type="text"
          placeholder="Type a message..."
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button
          style={styles.sendBtn}
          onClick={handleSend}
          disabled={!inputText.trim()}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
        </button>
      </div>
    </div>
    </>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f111a 0%, #171b26 100%)',
    fontFamily: "'Inter', sans-serif",
    padding: 20,
  },
  card: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 24,
    padding: '40px 32px',
    maxWidth: 400,
    width: '100%',
    textAlign: 'center',
    backdropFilter: 'blur(10px)',
  },
  inviteIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  title: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 700,
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#a0aab2',
    fontSize: 16,
    margin: '0 0 24px 0',
  },
  label: {
    color: '#a0aab2',
    fontSize: 14,
    marginBottom: 12,
    textAlign: 'left',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.05)',
    color: '#fff',
    fontSize: 16,
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 16,
    fontFamily: 'inherit',
  },
  button: {
    width: '100%',
    padding: '14px',
    borderRadius: 12,
    border: 'none',
    background: 'linear-gradient(135deg, #ff8a00, #e52e71)',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'transform 0.2s',
  },
  loadingSpinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTop: '3px solid #ff8a00',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  loadingText: {
    color: '#a0aab2',
    fontSize: 14,
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  errorTitle: {
    color: '#fca5a5',
    fontSize: 24,
    fontWeight: 700,
    margin: '0 0 8px 0',
  },
  errorText: {
    color: '#a0aab2',
    fontSize: 14,
    marginBottom: 24,
  },
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#0f111a',
    color: '#fff',
    fontFamily: "'Inter', sans-serif",
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'rgba(25,28,41,0.8)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    marginRight: 12,
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  headerName: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    display: 'inline-block',
  },
  statusText: {
    fontSize: 12,
    color: '#a0aab2',
  },
  messagesArea: {
    flex: 1,
    padding: 20,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  messageBubble: {
    maxWidth: '75%',
    padding: '12px 16px',
    borderRadius: 20,
    fontSize: 15,
    lineHeight: 1.4,
    color: '#fff',
  },
  emptyState: {
    textAlign: 'center',
    color: '#a0aab2',
    padding: 40,
  },
  inputArea: {
    display: 'flex',
    padding: 16,
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
    background: 'rgba(25,28,41,0.8)',
    backdropFilter: 'blur(10px)',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    gap: 12,
  },
  messageInput: {
    flex: 1,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 24,
    padding: '12px 20px',
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: 15,
    outline: 'none',
  },
  sendBtn: {
    background: 'linear-gradient(135deg, #ff8a00, #e52e71)',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: 46,
    height: 46,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};

export default InvitePage;
